import { supabase } from '@/integrations/supabase/client';
import { Lead, OpeningHours, OpeningPeriod } from '@/types/lead';

export interface PlaceReview {
  author_name: string;
  author_url: string;
  language: string;
  profile_photo_url: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
}

export interface PlaceDetailsResponse {
  result: {
    name: string;
    rating: number;
    user_ratings_total: number;
    reviews: PlaceReview[];
    formatted_address: string;
    formatted_phone_number: string;
    website: string;
    photos: Array<{
      photo_reference: string;
      height: number;
      width: number;
    }>;
    opening_hours?: {
      open_now?: boolean;
      periods: Array<{
        open: {
          day: number;
          time: string;
        };
        close?: {
          day: number;
          time: string;
        };
      }>;
      weekday_text: string[];
    };
  };
  status: string;
}

export const getPlaceDetails = async (placeId: string): Promise<PlaceDetailsResponse | null> => {
  try {
    console.log(`Fetching place details for placeId: ${placeId}`);
    
    const { data, error } = await supabase.functions.invoke('place-details', {
      body: { placeId },
    });

    if (error) {
      console.error('Error fetching place details:', error);
      throw error;
    }
    
    console.log('Received place details response:', data);
    return data;
  } catch (error) {
    console.error('Error in getPlaceDetails:', error);
    return null;
  }
};

export const getPlaceIdFromLead = (lead: Lead): string | null => {
  if (lead.placeId) {
    console.log(`Using placeId directly from lead: ${lead.placeId}`);
    return lead.placeId;
  }
  
  if (lead.webSearchData && typeof lead.webSearchData === 'object') {
    const webData = lead.webSearchData as any;
    
    if (webData.place_id) {
      console.log(`Found place_id directly in webSearchData: ${webData.place_id}`);
      return webData.place_id;
    }
    
    if (webData.google && webData.google.place_id) {
      console.log(`Found place_id in google object: ${webData.google.place_id}`);
      return webData.google.place_id;
    }
    
    if (webData.places && Array.isArray(webData.places) && webData.places.length > 0) {
      if (webData.places[0].place_id) {
        console.log(`Found place_id in places array: ${webData.places[0].place_id}`);
        return webData.places[0].place_id;
      }
    }
    
    if (webData.results && Array.isArray(webData.results) && webData.results.length > 0) {
      if (webData.results[0].place_id) {
        console.log(`Found place_id in results array: ${webData.results[0].place_id}`);
        return webData.results[0].place_id;
      }
    }
  }
  
  console.log('No place_id found in lead data');
  return null;
};

export const updateLeadWithPlaceDetails = async (lead: Lead): Promise<Lead | null> => {
  const placeId = getPlaceIdFromLead(lead);
  
  if (!placeId) {
    console.error('Cannot update lead: No place_id available');
    return null;
  }
  
  try {
    const placeDetails = await getPlaceDetails(placeId);
    
    if (!placeDetails || placeDetails.status !== 'OK') {
      console.error('Failed to fetch place details for updating lead');
      return null;
    }
    
    const result = placeDetails.result;
    
    let openingHours: OpeningHours | undefined = undefined;
    if (result.opening_hours) {
      openingHours = {
        periods: result.opening_hours.periods.map((period: any) => ({
          open: {
            day: period.open.day,
            time: period.open.time,
          },
          close: period.close ? {
            day: period.close.day,
            time: period.close.time,
          } : {
            day: 0,
            time: '0000'
          },
        })),
        weekdayText: result.opening_hours.weekday_text || [],
        openNow: result.opening_hours.open_now
      };
    }
    
    const updatedLead: Lead = {
      ...lead,
      name: result.name || lead.name,
      address: result.formatted_address || lead.address,
      phone: result.formatted_phone_number || lead.phone,
      website: result.website || lead.website,
      rating: result.rating || lead.rating,
      reviewCount: result.user_ratings_total || lead.reviewCount,
      placeId: placeId,
      openingHours: openingHours
    };
    
    return updatedLead;
  } catch (error) {
    console.error('Error updating lead with place details:', error);
    return null;
  }
};

export const transformGoogleDataToOpeningHours = (placeData: any) => {
  if (!placeData || !placeData.opening_hours) return undefined;
  
  const openingHours = {
    periods: placeData.opening_hours.periods.map((period: any) => ({
      open: {
        day: period.open.day,
        time: period.open.time,
      },
      close: period.close ? {
        day: period.close.day,
        time: period.close.time,
      } : { day: 0, time: '0000' },
    })) as OpeningPeriod[],
    weekdayText: placeData.opening_hours.weekday_text || [],
    openNow: placeData.opening_hours.open_now || false,
  };
  
  return openingHours;
};
